<template>
  <LazyProductDetailPageRelationsSection
    v-if="products && products.items!.length > 0"
    :ref="config.public.gtm.SELECTED_PRODUCTS_LIST"
    :tracking-position="config.public.gtm.SELECTED_PRODUCTS_LIST"
    :relation="products"
    :is-price-loading="pending"
    :is-stock-loading="pending"
    :is-plain-tiles="isPlainTiles"
  />
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import gql from '@/gql/selected-products';
import { ProductRelation, ProductRelationItem } from '@/types/products';
import { useUserStore, useDeliveryStore } from '@/stores';

type Props = {
  content: Record<string, any>;
};

const props = defineProps<Props>();

const route = useRoute();
const config = useRuntimeConfig();
const { isLoggedIn } = storeToRefs(useUserStore());
const { getSelectedDeliveryId, getStockInput, getCurrentStockLocations } = storeToRefs(useDeliveryStore());
const { $campaignService, $apiGateway, $sentry } = useNuxtApp();
const { getPreferredStore } = storeToRefs(useUserStore());

const isPlainTiles = computed(() => route.name === 'index' || !isLoggedIn.value);

const { data, pending } = await useAsyncData(
  `selectedProducts-${props.content?.fields?.selectedProductsId}`,
  async () => {
    try {
      const { data, error } = await $campaignService.getSelectedProducts<{
        articleNumbers: string[];
        error: any;
      }>(props.content.fields.selectedProductsId);

      if (!data?.articleNumbers) {
        return;
      }

      if (error) throw error;

      return $apiGateway.post<{ data: { data: { variants: ProductRelationItem[] } } }>('/graphql', {
        query: gql.products,
        variables: {
          articleNumbers: data!.articleNumbers,
          deliveryId: getSelectedDeliveryId.value,
          stockInput: getStockInput.value,
          skipStock: !getCurrentStockLocations.value?.length || isPlainTiles.value,
          skipPrice: !getCurrentStockLocations.value?.length || isPlainTiles.value,
          locationId: getPreferredStore.value.id,
        },
      });
    } catch (error) {
      $sentry.captureException(error);
    }
  },
  { watch: [getCurrentStockLocations], transform: (res) => res?.data?.data },
);

const products = computed<ProductRelation>(() => {
  return { items: data.value?.variants.filter(Boolean) || [], label: props.content.fields.name };
});
</script>

<script lang="ts">
export default defineComponent({
  name: 'SelectedProducts',
});
</script>
